import { useEffect, useRef, useState } from "react";
import { ArrowRight, Heart, Star } from "lucide-react";
import { services } from "../data";

export default function Services() {
  const sectionRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Sort: Featured first
  const sortedServices = [
    ...services.filter(s => s.featured),
    ...services.filter(s => !s.featured)
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-up");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleBookAppointment = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section ref={sectionRef} id="services" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 animate-on-scroll opacity-0">
          <div className="inline-flex items-center px-4 py-2 bg-[#4a7c59]/10 rounded-full mb-4">
            <Heart className="w-4 h-4 text-[#4a7c59] mr-2" />
            <span className="text-[#4a7c59] text-sm font-semibold">What We Offer</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gray-800">Our Premium </span>
            <span className="text-gradient-olive">Services</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
            Comprehensive dental care tailored to your needs with cutting-edge technology and gentle approach
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {sortedServices.map((service, idx) => (
            <div
              key={service.id}
              className="animate-on-scroll opacity-0 group relative"
              style={{ transitionDelay: `${idx * 100}ms` }}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className={`absolute -inset-1 bg-gradient-to-r ${service.color} rounded-2xl blur opacity-0 group-hover:opacity-30 transition duration-500`}></div>
              <div className={`relative bg-white rounded-xl md:rounded-2xl shadow-lg overflow-hidden transform transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-2xl ${service.bgHover} h-full`}>
                
                {/* REMOVED: Popular Badge */}

                <div className="p-4 md:p-6 text-center">
                  <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-3 md:mb-4 rounded-xl overflow-hidden shadow-md">
                    <img 
                      src={service.image} 
                      alt={service.imageAlt}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  
                  <h3 className="text-sm md:text-xl font-bold text-gray-800 mb-1 md:mb-2">{service.name}</h3>
                  
                  <p className="text-gray-500 text-xs md:text-sm mb-3 md:mb-4 leading-relaxed line-clamp-3">
                    {service.description}
                  </p>
                  
                  {/* FIXED: "Book Now" with clean design */}
                  <button 
                    onClick={handleBookAppointment}
                    className={`w-full px-3 py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-1.5 bg-gray-100 text-gray-600 hover:text-white hover:bg-gradient-to-r ${service.color}`}
                  >
                    Book Now
                    <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform duration-300" />
                  </button>
                </div>
                
                <div className={`absolute inset-0 bg-gradient-to-r ${service.color} transform transition-transform duration-500 ${hoveredIndex === idx ? 'translate-y-0' : 'translate-y-full'}`} style={{ top: 'auto', height: '3px' }}></div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center animate-on-scroll opacity-0">
          <div className="inline-flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-white rounded-full shadow-md flex-wrap justify-center">
            <Star className="w-4 h-4 md:w-5 md:h-5 text-yellow-500 fill-yellow-500" />
            <span className="text-gray-700 text-xs md:text-sm">All treatments performed by experienced BDS specialists</span>
            <Star className="w-4 h-4 md:w-5 md:h-5 text-yellow-500 fill-yellow-500" />
          </div>
        </div>

        <div className="mt-8 md:mt-10 text-center animate-on-scroll opacity-0">
          <div className="inline-flex items-center gap-3 px-6 py-3 md:px-8 md:py-4 bg-gradient-to-r from-[#4a7c59]/10 to-[#2d5a3f]/10 rounded-2xl border border-[#4a7c59]/20">
            <Heart className="w-4 h-4 md:w-5 md:h-5 text-[#4a7c59]" />
            <span className="text-gray-700 text-sm md:text-base font-medium">Need Help Choosing a Treatment?</span>
            <button 
              onClick={handleBookAppointment}
              className="px-4 md:px-6 py-1.5 md:py-2 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] text-white rounded-full text-xs md:text-sm font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
            >
              Book Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}